import React from 'react';
import { Button, NumberInput, Switch, useToast } from '@parsina/framework';
import { C } from '../../constants/theme.js';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { type } from '../../constants/typography.js';
import { useLowBalanceConfig } from '../../hooks/useCredits.js';

/**
 * هشدار اعتبار کم — the wallet's own alert, edited where it is read.
 *
 * The doctor panel puts this behind a link to a settings tab. There is no
 * credits tab on پروفایل here, and there should not be one for a single
 * threshold: a setting that only makes sense while looking at a balance belongs
 * beside the balance. So the whole control is on the card, and it commits on a
 * button rather than on every keystroke — a half-typed «۲» would otherwise mean
 * "warn me below two پارسه", which is never what anyone meant.
 *
 * The number here is NOT the red floor this screen paints below 50 پارسه
 * (src/constants/credit.js). That one is a client rule about colour; this one is
 * what the wallet sends an SMS about.
 */
export default function LowBalanceCard() {
  const { t, formatNumber } = useTranslation();
  const toast = useToast();
  const { config, loading, saving, save } = useLowBalanceConfig();

  const [threshold, setThreshold] = React.useState('');
  const [enabled, setEnabled] = React.useState(true);

  React.useEffect(() => {
    if (!config) return;
    setThreshold(String(Math.round(config.threshold)));
    setEnabled(config.enableAlerts);
  }, [config]);

  const dirty = !!config
    && (Number(threshold) !== Math.round(config.threshold) || enabled !== config.enableAlerts);

  const commit = async () => {
    const value = Number(threshold);
    if (!Number.isFinite(value) || value < 0) {
      toast.error(t('credits.lowBalance.invalid'));
      return;
    }
    const ok = await save({ threshold: value, enableAlerts: enabled });
    if (ok) toast.success(t('credits.lowBalance.saved'));
    else toast.error(t('credits.lowBalance.failed'));
  };

  return (
    <div
      data-testid="credits-lowbalance"
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 16,
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...type.title, color: C.textPrimary, flex: 1 }}>
          {t('credits.lowBalance.title')}
        </span>
        <Switch
          checked={enabled}
          onChange={setEnabled}
          disabled={loading}
          label={t('credits.lowBalance.toggleAria')}
        />
      </div>

      <div style={{ ...type.caption, color: C.textMuted, lineHeight: 1.9 }}>
        {t('credits.lowBalance.hint')}
      </div>

      <NumberInput
        value={threshold}
        // The kit's NumberInput reports `{ target: { value } }`, having already
        // folded Persian digits to Latin — so a threshold typed as «۱۵۰» arrives
        // as '150' rather than as NaN.
        onChange={(e) => setThreshold(e.target.value)}
        unit={t('credits.unit')}
        min={0}
        disabled={loading || !enabled}
        aria-label={t('credits.lowBalance.thresholdAria')}
        data-testid="credits-lowbalance-threshold"
      />

      <Button
        onClick={commit}
        disabled={!dirty || saving || loading}
        variant="outline"
        size="sm"
        data-testid="credits-lowbalance-save"
      >
        {saving ? t('common.actions.saving') : t('common.actions.save')}
      </Button>

      {config && !dirty && (
        <div style={{ ...type.micro, color: C.textMuted }}>
          {enabled
            ? t('credits.lowBalance.active', { coins: formatNumber(Math.round(config.threshold)) })
            : t('credits.lowBalance.off')}
        </div>
      )}
    </div>
  );
}
